// PRPathways.js
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, MapPin, Users, ChevronDown, ChevronUp } from 'lucide-react';

const PRPathways = () => {
  const [expandedPathway, setExpandedPathway] = useState(null);

  const pathways = [
    {
      icon: Zap,
      title: 'Express Entry',
      subtitle: 'Federal Skilled Worker, Canadian Experience Class, Federal Skilled Trades',
      description: 'The fastest and most popular pathway to Canadian PR, processing applications in as little as 6 months.',
      details: {
        overview: 'Express Entry is a points-based system that manages applications for three federal economic immigration programs.',
        programs: [
          {
            name: 'Federal Skilled Worker Program (FSWP)',
            requirements: 'For skilled workers with foreign work experience. Requires minimum 1 year of continuous full-time work experience in a skilled occupation (NOC TEER 0, 1, 2, or 3).'
          },
          {
            name: 'Canadian Experience Class (CEC)',
            requirements: 'For individuals with Canadian work experience. Requires minimum 1 year of skilled work experience in Canada within the last 3 years.'
          },
          {
            name: 'Federal Skilled Trades Program (FSTP)',
            requirements: 'For skilled tradespeople. Requires 2 years of full-time work experience in a skilled trade within the last 5 years and a valid job offer or certificate of qualification.'
          }
        ],
        keyPoints: [
          'Comprehensive Ranking System (CRS) score determines eligibility',
          'Regular draws conducted every 2 weeks',
          'No job offer required for most applicants',
          'Processing time: 6 months or less',
          'Minimum CRS score varies (typically 470-500 in recent draws)'
        ]
      }
    },
    {
      icon: MapPin,
      title: 'Provincial Nominee Programs (PNPs)',
      subtitle: 'Ontario, British Columbia, Alberta, Saskatchewan, Manitoba, and more',
      description: 'Province-specific programs allowing provinces to nominate individuals who wish to settle in their region.',
      details: {
        overview: 'Each Canadian province (except Quebec) has its own PNP with unique streams targeting specific skills and occupations.',
        programs: [
          {
            name: 'Ontario Immigrant Nominee Program (OINP)',
            requirements: 'Various streams including Human Capital Priorities, French-Speaking Skilled Worker, and Employer Job Offer streams. Ontario conducts targeted draws based on NOC codes and CRS scores.'
          },
          {
            name: 'British Columbia PNP (BC PNP)',
            requirements: 'Skills Immigration and Express Entry BC streams. Requires job offer from BC employer in most streams. Tech sector has dedicated draws.'
          },
          {
            name: 'Alberta Immigrant Nominee Program (AINP)',
            requirements: 'Alberta Opportunity Stream and Express Entry Stream. Focus on candidates with Alberta work experience or job offers.'
          },
          {
            name: 'Saskatchewan Immigrant Nominee Program (SINP)',
            requirements: 'International Skilled Worker category with occupation in-demand and Express Entry sub-categories. No job offer required for some streams.'
          },
          {
            name: 'Manitoba Provincial Nominee Program (MPNP)',
            requirements: 'Skilled Workers in Manitoba and Skilled Workers Overseas streams. Manitoba connection or job offer typically required.'
          }
        ],
        keyPoints: [
          'Provincial nomination adds 600 points to CRS score',
          'Each province has unique eligibility criteria',
          'Some streams require job offer, others do not',
          'Processing time: 15-19 months total',
          'Great option for candidates with lower CRS scores'
        ]
      }
    },
    {
      icon: Users,
      title: 'Family Sponsorship',
      subtitle: 'Spouse, Common-Law Partner, Dependent Children, Parents, Grandparents',
      description: 'Canadian citizens and permanent residents can sponsor eligible family members for permanent residency.',
      details: {
        overview: 'Family sponsorship allows Canadian citizens and PRs to bring their close family members to Canada.',
        programs: [
          {
            name: 'Spousal and Common-Law Partner Sponsorship',
            requirements: 'Sponsor must be 18+ and a Canadian citizen or PR. Can apply from inside or outside Canada. Must prove genuine relationship and meet minimum income requirements (except for spouse/partner sponsorship).'
          },
          {
            name: 'Dependent Children Sponsorship',
            requirements: 'Children under 22 who are not married or in common-law relationship. Children 22+ must be dependent due to physical or mental condition.'
          },
          {
            name: 'Parents and Grandparents Program (PGP)',
            requirements: 'Sponsor must meet minimum necessary income for 3 consecutive years. Limited intake through lottery system. Super Visa available as alternative for extended visits.'
          }
        ],
        keyPoints: [
          'Spousal sponsorship processing: 12 months',
          'Parents/Grandparents processing: 20-24 months',
          'Sponsor must sign undertaking to support sponsored person',
          'No points system - relationship proof is key',
          'Sponsored person gets full PR status upon approval'
        ]
      }
    }
  ];

  const togglePathway = (index) => {
    setExpandedPathway(expandedPathway === index ? null : index);
  };

  return (
    <section className="py-16 bg-[#F5F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-[#202356] mb-4">
            Available PR Pathways
          </h2>
          <p className="text-gray-600 text-base font-light max-w-3xl mx-auto">
            Choose the pathway that best matches your profile, experience, and immigration goals.
          </p>
        </motion.div>

        {/* Pathways */}
        <div className="space-y-6">
          {pathways.map((pathway, index) => {
            const Icon = pathway.icon;
            const isExpanded = expandedPathway === index;

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-sm overflow-hidden"
              >
                {/* Pathway Header */}
                <button
                  onClick={() => togglePathway(index)}
                  className="w-full p-6 flex items-start gap-4 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-[#202356] to-[#2542D4] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon size={24} className="text-white" />
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-[#202356] mb-2">
                      {pathway.title}
                    </h3>
                    <p className="text-sm text-gray-500 font-light mb-2">
                      {pathway.subtitle}
                    </p>
                    <p className="text-gray-600 text-sm font-light">
                      {pathway.description}
                    </p>
                  </div>

                  <div className="flex-shrink-0">
                    {isExpanded ? (
                      <ChevronUp size={24} className="text-[#2542D4]" />
                    ) : (
                      <ChevronDown size={24} className="text-[#2542D4]" />
                    )}
                  </div>
                </button>

                {/* Expanded Details */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6 border-t border-gray-200">
                        <div className="pt-6 space-y-6">
                          {/* Overview */}
                          <div>
                            <h4 className="text-base font-semibold text-[#202356] mb-2">
                              Overview
                            </h4>
                            <p className="text-gray-600 text-sm font-light leading-relaxed">
                              {pathway.details.overview}
                            </p>
                          </div>

                          {/* Programs/Categories */}
                          <div>
                            <h4 className="text-base font-semibold text-[#202356] mb-3">
                              Programs & Requirements
                            </h4>
                            <div className="space-y-4">
                              {pathway.details.programs.map((program, pIndex) => (
                                <div key={pIndex} className="bg-[#F5F8FA] p-4 rounded-lg">
                                  <h5 className="text-sm font-semibold text-[#202356] mb-2">
                                    {program.name}
                                  </h5>
                                  <p className="text-gray-600 text-sm font-light leading-relaxed">
                                    {program.requirements}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Key Points */}
                          <div>
                            <h4 className="text-base font-semibold text-[#202356] mb-3">
                              Key Points
                            </h4>
                            <ul className="space-y-2">
                              {pathway.details.keyPoints.map((point, kIndex) => (
                                <li key={kIndex} className="flex items-start gap-2">
                                  <span className="w-1.5 h-1.5 bg-[#2542D4] rounded-full mt-2 flex-shrink-0"></span>
                                  <span className="text-gray-600 text-sm font-light">
                                    {point}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PRPathways;
